import {
  isRisingDrawdownIntervalWithinDuration,
  RISING_DRAWDOWN_INTERVAL_EXCEEDS_DURATION_MESSAGE,
  RisingDrawdownEliminationConfigSchema,
} from "../paper-trading/paper-trading-config";
import { ARENA_MAX_PAIR_SYMBOLS } from "../trade-to-earn/admin-trade-to-earn-campaign.dto";
import {
  BATTLE_ROYALE_INTERVAL_MAX_SECONDS,
  BATTLE_ROYALE_INTERVAL_MIN_SECONDS,
  BATTLE_ROYALE_MAX_PLAYERS,
  BATTLE_ROYALE_MIN_PLAYERS,
  BattleRoyaleEliminationRuleReadSchema,
  BattleRoyaleEliminationRuleSchema,
} from "./battle-royale";
import { ArenaCreatorRakeBpsSchema, ArenaRakeBpsSchema } from "./rake";
import Decimal from "decimal.js";
import { z } from "zod";

/**
 * Cede Arena private competitions — user-generated competition rooms.
 *
 * A hub user creates a room, shares its invite link, friends join in a live
 * waiting room, then the owner sets the prize split and starts, materializing
 * a paper/perps PnL competition restricted to the room members.
 *
 * Money fields cross the wire as decimal STRINGS (never JS `number`) so the
 * payloads stay JSON-serializable and free of float drift; the backend domain
 * parses them into `Decimal` for the prize split math.
 */

const isPositiveDecimal = (value: string): boolean => {
  try {
    const decimal = new Decimal(value);
    // `Decimal("Infinity")` / `Decimal("NaN")` construct successfully and pass
    // gt/gte — reject them explicitly so non-finite money never crosses the wire.
    return decimal.isFinite() && decimal.gt(0);
  } catch {
    return false;
  }
};

const isNonNegativeDecimal = (value: string): boolean => {
  try {
    const decimal = new Decimal(value);
    return decimal.isFinite() && decimal.gte(0);
  } catch {
    return false;
  }
};

const isZeroDecimal = (value: string): boolean => {
  try {
    return new Decimal(value).isZero();
  } catch {
    return false;
  }
};

const PositiveDecimalStringSchema = z
  .string()
  .min(1)
  .refine(isPositiveDecimal, { message: "must be a positive decimal string" });

const NonNegativeDecimalStringSchema = z
  .string()
  .min(1)
  .refine(isNonNegativeDecimal, { message: "must be a non-negative decimal string" });

/** A trading pair symbol, perps form `BASE/QUOTE:QUOTE` (e.g. `BTC/USDT:USDT`). */
const PrivateCompetitionPairSymbolSchema = z
  .string()
  .regex(/^[^/]+\/[^/]+$/, { message: "must be a pair symbol like BTC/USDT:USDT" });

/**
 * One weighted place group in the prize split: `quantity` places each weighing
 * `reward`. Weights need not add up to anything; at start the pot is scaled
 * across them by `distributePrizePot`.
 */
const PrivateCompetitionSlotSchema = z.object({
  quantity: z.number().int().positive(),
  reward: PositiveDecimalStringSchema,
});
export type PrivateCompetitionSlotDto = z.infer<typeof PrivateCompetitionSlotSchema>;

export const PrivateCompetitionFormatSchema = z.enum(["CLASSIC", "BATTLE_ROYALE"]);
export type PrivateCompetitionFormat = z.infer<typeof PrivateCompetitionFormatSchema>;

/**
 * Owner-configured competition settings. `customSlots` is the prize split as
 * weights; absent means the winner takes the whole pool.
 */
const PrivateCompetitionRoomConfigBaseSchema = z.object({
  /** Fixed at room creation. */
  format: PrivateCompetitionFormatSchema.default("CLASSIC"),
  // Battle Royale sends durationSeconds = eliminationIntervalSeconds; the window derives from the roster instead.
  durationSeconds: z.number().int().positive(),
  /** `"0"` is allowed: a free competition (no buy-in, no prize pool, bragging rights only). */
  entryFeesUsdt: NonNegativeDecimalStringSchema,
  minVolumeUsd: NonNegativeDecimalStringSchema.default("0"),
  initialDepositUsd: z.number().positive(),
  pairSymbols: z
    .array(PrivateCompetitionPairSymbolSchema)
    .min(1)
    .max(ARENA_MAX_PAIR_SYMBOLS)
    .refine((pairs) => new Set(pairs).size === pairs.length, { message: "pair symbols must be unique" }),
  customSlots: z.array(PrivateCompetitionSlotSchema).min(1).optional(),
  /**
   * The creator's OWN rake (basis points, 0–2000 = 0%–20%, 0.25% steps), taken
   * off the pool on top of the platform rake and credited to the creator. Set by
   * the owner in the room-create flow; snapshotted onto the competition at start.
   * Absent/0 ⇒ no creator rake.
   */
  creatorRakeBps: ArenaCreatorRakeBpsSchema.optional().default(0),
  /** Battle Royale only: whole seconds between eliminations. */
  eliminationIntervalSeconds: z
    .number()
    .int()
    .min(BATTLE_ROYALE_INTERVAL_MIN_SECONDS)
    .max(BATTLE_ROYALE_INTERVAL_MAX_SECONDS)
    .nullish(),
  /** Battle Royale only: warm-up before elimination 1 and the final 1v1's length; absent = the interval. */
  firstEliminationSeconds: z
    .number()
    .int()
    .min(BATTLE_ROYALE_INTERVAL_MIN_SECONDS)
    .max(BATTLE_ROYALE_INTERVAL_MAX_SECONDS)
    .nullish(),
  finalEliminationSeconds: z
    .number()
    .int()
    .min(BATTLE_ROYALE_INTERVAL_MIN_SECONDS)
    .max(BATTLE_ROYALE_INTERVAL_MAX_SECONDS)
    .nullish(),
  /** Battle Royale only: how many players each elimination cuts; absent = one. */
  elimination: BattleRoyaleEliminationRuleSchema.nullish(),
  /** Battle Royale only: keep the last elimination a 1v1; absent = on. */
  finalDuel: z.boolean().nullish(),
  /** Seat cap for any format; `null` = unlimited. */
  maxParticipants: z.number().int().min(2).nullish(),
  /** Classic only: the rising-drawdown format; absent ⇒ the global CMS drawdown policy applies. */
  drawdown: RisingDrawdownEliminationConfigSchema.optional(),
});

function refineRoomConfig(config: z.infer<typeof PrivateCompetitionRoomConfigBaseSchema>, ctx: z.RefinementCtx): void {
  if (config.format !== "BATTLE_ROYALE") {
    for (const key of [
      "eliminationIntervalSeconds",
      "firstEliminationSeconds",
      "finalEliminationSeconds",
      "elimination",
      "finalDuel",
    ] as const) {
      if (config[key] != null) ctx.addIssue({ code: "custom", path: [key], message: "only a Battle Royale has one" });
    }
    if (
      config.drawdown !== undefined &&
      !isRisingDrawdownIntervalWithinDuration(config.drawdown.stepIntervalSeconds, config.durationSeconds)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["drawdown", "stepIntervalSeconds"],
        message: RISING_DRAWDOWN_INTERVAL_EXCEEDS_DURATION_MESSAGE,
      });
    }
    return;
  }
  if (config.drawdown !== undefined) {
    ctx.addIssue({ code: "custom", path: ["drawdown"], message: "Battle Royale cannot be combined with drawdown" });
  }
  if (config.eliminationIntervalSeconds == null) {
    ctx.addIssue({ code: "custom", path: ["eliminationIntervalSeconds"], message: "required for a Battle Royale" });
  }
  if (!isZeroDecimal(config.minVolumeUsd)) {
    ctx.addIssue({ code: "custom", path: ["minVolumeUsd"], message: "a Battle Royale has no min volume" });
  }
  if (config.maxParticipants != null && config.maxParticipants < BATTLE_ROYALE_MIN_PLAYERS) {
    ctx.addIssue({
      code: "custom",
      path: ["maxParticipants"],
      message: `a Battle Royale needs room for at least ${BATTLE_ROYALE_MIN_PLAYERS} players`,
    });
  }
}

export const PrivateCompetitionRoomConfigSchema = PrivateCompetitionRoomConfigBaseSchema.superRefine(refineRoomConfig);
/** For responses: an elimination-rule key a newer API added is stripped instead of failing the whole room. */
export const PrivateCompetitionRoomConfigReadSchema = PrivateCompetitionRoomConfigBaseSchema.extend({
  elimination: BattleRoyaleEliminationRuleReadSchema.nullish(),
}).superRefine(refineRoomConfig);
export type PrivateCompetitionRoomConfig = z.infer<typeof PrivateCompetitionRoomConfigSchema>;

// One seat cap for every format, the Battle Royale's.
export const PRIVATE_COMPETITION_MAX_PLAYERS = BATTLE_ROYALE_MAX_PLAYERS;

// Far above any real split; they keep a hostile one from costing every viewer of the room.
export const PRIVATE_COMPETITION_MAX_SPLIT_ROWS = 100;
export const PRIVATE_COMPETITION_MAX_PLACES_PER_ROW = 1000;
// No sign or exponent: a weight like "1e8999999999999999" overflows once scaled to the pot.
const PLAIN_SPLIT_WEIGHT = /^\d{1,12}(\.\d{1,8})?$/;
// Fill volume sums into numeric(20,8) aggregates (< $1T): at 40x, a $1B balance fills ~$40B an order.
export const PRIVATE_COMPETITION_MAX_INITIAL_DEPOSIT_USD = 1_000_000;

/** A config a request writes: the stored schema plus the caps below, which a stored room is not re-checked against. */
export const PrivateCompetitionRoomConfigInputSchema = PrivateCompetitionRoomConfigSchema.superRefine((config, ctx) => {
  if (config.maxParticipants != null && config.maxParticipants > PRIVATE_COMPETITION_MAX_PLAYERS) {
    ctx.addIssue({
      code: "custom",
      path: ["maxParticipants"],
      message: `a room takes at most ${PRIVATE_COMPETITION_MAX_PLAYERS} players`,
    });
  }
  if (config.initialDepositUsd > PRIVATE_COMPETITION_MAX_INITIAL_DEPOSIT_USD) {
    ctx.addIssue({
      code: "custom",
      path: ["initialDepositUsd"],
      message: `a starting balance is at most ${PRIVATE_COMPETITION_MAX_INITIAL_DEPOSIT_USD} USD`,
    });
  }
  const customSlots = config.customSlots ?? [];
  if (customSlots.length > PRIVATE_COMPETITION_MAX_SPLIT_ROWS) {
    ctx.addIssue({
      code: "custom",
      path: ["customSlots"],
      message: `a prize split takes at most ${PRIVATE_COMPETITION_MAX_SPLIT_ROWS} rows`,
    });
  }
  customSlots.forEach((slot, index) => {
    if (slot.quantity > PRIVATE_COMPETITION_MAX_PLACES_PER_ROW) {
      ctx.addIssue({
        code: "custom",
        path: ["customSlots", index, "quantity"],
        message: `a prize split row takes at most ${PRIVATE_COMPETITION_MAX_PLACES_PER_ROW} places`,
      });
    }
    if (!PLAIN_SPLIT_WEIGHT.test(slot.reward)) {
      ctx.addIssue({ code: "custom", path: ["customSlots", index, "reward"], message: "must be a plain decimal" });
    }
  });
});

export const PRIVATE_COMPETITION_ENTRY_FEE_DECIMALS = 2;

/** The pot floors to cents, so a finer fee would collect money no place is paid. */
export const isEntryFeeInWholeCents = (entryFeesUsdt: string): boolean => {
  try {
    return new Decimal(entryFeesUsdt).decimalPlaces() <= PRIVATE_COMPETITION_ENTRY_FEE_DECIMALS;
  } catch {
    return false;
  }
};

export const PrivateCompetitionRoomStatusSchema = z.enum(["WAITING", "STARTED", "CANCELLED"]);
export type PrivateCompetitionRoomStatus = z.infer<typeof PrivateCompetitionRoomStatusSchema>;

export const PrivateCompetitionParticipantDtoSchema = z.object({
  hubUserId: z.number().int().positive(),
  /** Join order; the owner is seat 0. Drives stable roster rendering. */
  seat: z.number().int().nonnegative(),
  /** User-set Arena nickname (`HubUser.nickname`); `null` until set. */
  nickname: z.string().nullable(),
  isOwner: z.boolean(),
});
export type PrivateCompetitionParticipantDto = z.infer<typeof PrivateCompetitionParticipantDtoSchema>;

/**
 * Full room view returned by `GET /v1/cede-arena/private/rooms/:slug` and as
 * the response of every mutating endpoint. The viewer-relative flags are
 * computed per request from the authenticated caller (`null` viewer for the
 * public SSR fetch).
 */
export const PrivateCompetitionRoomDtoSchema = z.object({
  slug: z.string(),
  status: PrivateCompetitionRoomStatusSchema,
  ownerHubUserId: z.number().int().positive(),
  config: PrivateCompetitionRoomConfigReadSchema,
  /**
   * Platform rake (basis points) applied to this room's pool before payout.
   * A global CMS policy — NOT owner-set — snapshotted onto the room at creation
   * so joiners see a stable, locked figure before opting in. The FE derives the
   * post-rake pool via `applyArenaRake(entryFees × participants, rakeBps)`.
   */
  rakeBps: ArenaRakeBpsSchema,
  participants: z.array(PrivateCompetitionParticipantDtoSchema),
  /** Set once started — the competition campaign the members redirect to. */
  campaignName: z.string().nullable(),
  /**
   * Competition window, read from the linked campaign (`campaignName`). Both
   * `null` until the room starts. The frontend uses `endsAt` vs now to tell a
   * live competition ("in progress") from a finished one ("past").
   */
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  viewerHubUserId: z.number().int().positive().nullable(),
  isOwner: z.boolean(),
  isParticipant: z.boolean(),
  /** True only for the owner of a WAITING room with enough participants. */
  canStart: z.boolean(),
  /** Pool is resized by a Spin & Go multiplier drawn at start. */
  isSpinAndGo: z.boolean(),
  /**
   * The drawn pot multiplier, read from the linked campaign. Null until the room
   * is STARTED with a `campaignName` whose draw exists (and always null for a
   * non-spin room). The FE shows the realized multiplier once known.
   */
  spinAndGoPotFactor: z.string().nullable(),
});
export type PrivateCompetitionRoomDto = z.infer<typeof PrivateCompetitionRoomDtoSchema>;

// ── Request bodies ──────────────────────────────────────────────────────────

export const CreatePrivateCompetitionRoomRequestSchema = z.object({
  // New rooms only: an update re-sends the fee, and a legacy room's finer fee must still save.
  config: PrivateCompetitionRoomConfigInputSchema.refine((config) => isEntryFeeInWholeCents(config.entryFeesUsdt), {
    path: ["entryFeesUsdt"],
    message: `must have at most ${PRIVATE_COMPETITION_ENTRY_FEE_DECIMALS} decimals`,
  }),
  /** Resize the pool by a Spin & Go multiplier drawn at start (gated on the PRIVATE_ROOM category being enabled). */
  isSpinAndGo: z.boolean().optional(),
});
export type CreatePrivateCompetitionRoomRequest = z.infer<typeof CreatePrivateCompetitionRoomRequestSchema>;

export const UpdatePrivateCompetitionRoomConfigRequestSchema = z.object({
  config: PrivateCompetitionRoomConfigInputSchema,
});
export type UpdatePrivateCompetitionRoomConfigRequest = z.infer<typeof UpdatePrivateCompetitionRoomConfigRequestSchema>;

export const KickPrivateCompetitionParticipantRequestSchema = z.object({
  targetHubUserId: z.number().int().positive(),
});
export type KickPrivateCompetitionParticipantRequest = z.infer<typeof KickPrivateCompetitionParticipantRequestSchema>;

/** Default / max page size for the cursor-paginated "my private competitions" list. */
export const PRIVATE_COMPETITIONS_PAGE_SIZE = 20;
export const PRIVATE_COMPETITIONS_MAX_PAGE_SIZE = 50;

/** Response of `GET /v1/cede-arena/private/rooms` — the viewer's rooms (owner or participant), one page. */
export const MyPrivateCompetitionRoomsResponseSchema = z.object({
  rooms: z.array(PrivateCompetitionRoomDtoSchema),
  /** Opaque cursor (the last room's slug) to fetch the next page; `null` when there are no more. */
  nextCursor: z.string().nullable(),
});
export type MyPrivateCompetitionRoomsResponse = z.infer<typeof MyPrivateCompetitionRoomsResponseSchema>;
